import { auth, db } from '../firebase/config';
import { collection, addDoc, serverTimestamp, getDoc, getDocs, query, where, doc, writeBatch, increment } from 'firebase/firestore';
import { isAdminRole } from './roles';

const CLIENT_BAL = 'client_balance_entries';

export const appendClientBalanceEntry = async ({ clientId, date, amountUsd, bankLineId, memo, createdBy, isReversal = false, reversalOf = null }) => {
  const payload = { clientId, date, amountUsd: Number(amountUsd), bankLineId: bankLineId || null, memo: memo || '', isReversal, reversalOf, createdAt: serverTimestamp(), createdBy: createdBy || null };
  const ref = await addDoc(collection(db, CLIENT_BAL), payload);
  return { id: ref.id, ...payload };
};

export const getClientAvailableBalance = async (clientId) => {
  const q = query(collection(db, CLIENT_BAL), where('clientId', '==', clientId));
  const snap = await getDocs(q);
  let sum = 0;
  snap.forEach((d) => { sum += Number(d.data().amountUsd || 0); });
  return sum;
};

// Accept signed decimal dollars without silently truncating or rounding user input.
export const parseBalanceAdjustment = (value) => {
  const text = String(value ?? '').trim();
  if (!/^[+-]?(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(text)) return null;
  const amount = Number(text);
  if (!Number.isFinite(amount) || amount === 0 || !Number.isSafeInteger(Math.round(amount * 100))) return null;
  return amount;
};

export const summarizeClientBalances = (clients, entries, contracts) => {
  const funds = new Map();
  const applied = new Map();
  entries.forEach((entry) => {
    funds.set(entry.clientId, (funds.get(entry.clientId) || 0) + Math.round(Number(entry.amountUsd || 0) * 100));
  });
  contracts.forEach((contract) => {
    (Array.isArray(contract.milestoneLogs) ? contract.milestoneLogs : []).forEach((line) => {
      let amount = 0;
      if (line.status === 'Invoiced' || line.status === 'Partial') {
        amount = Number(line.appliedAmount || 0);
      } else if (line.status === 'Paid' && line.clientBalanceTracked === true) {
        const expected = Number(line.expectedAmount || 0);
        const actual = Number(line.actualAmount || 0);
        amount = actual > 0 ? Math.min(actual, expected) : expected;
      }
      applied.set(contract.clientEntityId, (applied.get(contract.clientEntityId) || 0) + Math.round(amount * 100));
    });
  });
  return clients.map((client) => ({
    ...client,
    fundsUsd: (funds.get(client.id) || 0) / 100,
    appliedUsd: (applied.get(client.id) || 0) / 100,
    balanceUsd: ((funds.get(client.id) || 0) - (applied.get(client.id) || 0)) / 100,
  })).sort((a, b) => String(a.clientName || '').localeCompare(String(b.clientName || '')));
};

export const adjustClientBalance = async ({ clientId, amountUsd, memo }) => {
  const amount = parseBalanceAdjustment(amountUsd);
  if (amount === null) throw new Error('Enter a non-zero signed amount with at most two decimal places.');
  if (!clientId) throw new Error('Client is required.');
  const createdBy = auth.currentUser?.uid;
  if (!createdBy) throw new Error('Sign in to adjust client balances.');
  let profile = await getDoc(doc(db, 'users', createdBy));
  if (!profile.exists()) {
    const matches = await getDocs(query(collection(db, 'users'), where('uid', '==', createdBy)));
    profile = matches.docs[0];
  }
  if (!isAdminRole(profile?.data()?.role)) throw new Error('Only Super Admins can adjust client balances.');

  const now = new Date();
  const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const timestamp = serverTimestamp();
  const entryRef = doc(collection(db, CLIENT_BAL));
  const batch = writeBatch(db);
  // Deliberately isolated from bank and ledger posting services.
  batch.set(entryRef, {
    clientId, date, amountUsd: amount, currency: 'USD', source: 'manual_adjustment',
    bankLineId: null, memo: String(memo || '').trim(), isReversal: false, reversalOf: null,
    createdAt: timestamp, createdBy,
  });
  batch.update(doc(db, 'clients', clientId), {
    availableBalance: increment(amount), availableBalanceUpdatedAt: timestamp,
  });
  await batch.commit();
  return { id: entryRef.id, amountUsd: amount };
};

export default { appendClientBalanceEntry, getClientAvailableBalance, adjustClientBalance };

